/* eslint-env node,browser */
import _ from 'lodash';
import Combinatorics from 'js-combinatorics';
import { nanoid } from 'nanoid';

import SharedConfig from 'shared/config';
import { DEVMODE } from 'shared/util';

import SimcInput, { COMMANDS_GEAR_ALIASES } from 'shared/simcinput';
import {
  itemSocketInfo,
  isLegionLegendary,
  getWeaponHands,
  gemInfo,
  getEnchant,
  canRollSocket,
  SOCKET_TYPES,
} from 'shared/items';
import { getSpecInfo } from 'shared/character';
import { talentsArmoryToSimc } from 'shared/talents';
import { parseAzeriteEssences, stringifyAzeriteEssences } from 'shared/azeriteEssences';
import { getSoulbind, getCovenant, parseSoulbindSimcString, getDominationShardSetBonus } from 'shared/shadowlands';
import GameData from 'shared/gameData';

function getNow() {
  if (typeof window !== 'undefined' && window.performance) {
    return window.performance.now();
  }
  return Date.now();
}

// eslint-disable-next-line no-unused-vars
function devLog(...args) {
  if (DEVMODE) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
}
// eslint-disable-next-line no-unused-vars
function devTable(data) {
  if (DEVMODE) {
    // eslint-disable-next-line no-console
    console.table(data);
  }
}

export function numLegendaries(items) {
  return _.reduce(items, (res, it) => {
    if (_.isArray(it)) {
      return res + numLegendaries(it);
    }
    if (isLegionLegendary(it)) {
      return res + 1;
    }
    return res;
  }, 0);
}

export function numItemSet(items, itemSetIds) {
  return _.reduce(items, (res, it) => {
    if (_.isArray(it)) {
      // skip rings/trinkets for now
      // TODO: if this needs to be done at some point, need to merge the results from a recursive call
      return res;
    }
    const { itemSetId } = it;
    if (itemSetId && _.includes(itemSetIds, itemSetId)) {
      if (!res[itemSetId]) {
        res[itemSetId] = 0;
      }
      res[itemSetId] += 1;
    }
    return res;
  }, {});
}

export function inputSource(input) {
  if (input) {
    if (input.match(/\[Profile\]/)) {
      return 'simpermut';
    }
    return 'simc';
  }
  return null;
}

export function transformInputToData(input) {
  if (!input) {
    return null;
  }
  let data = SimcInput.parse(input);
  if (inputSource(input) === 'simpermut') {
    const name = SimcInput.find(data, 'profilename');
    const charClass = SimcInput.find(data, 'class');

    data = SimcInput.reject(data, '[Profile]');
    data = SimcInput.reject(data, 'profilename');
    data = SimcInput.reject(data, 'profileid');
    data = SimcInput.reject(data, 'class');
    data = SimcInput.reject(data, 'position');
    data = SimcInput.reject(data, 'other');
    data = SimcInput.reject(data, '[Gear]');
    data = SimcInput.prepend(data, charClass, name);
    data = SimcInput.prependComment(data, 'Input from SimPermut');

    // handle inconsistent 2 ring/trinket autosimc output
    // merge the two items into finger1/trinket1 like /simc gear from bags
    const finger1 = SimcInput.find(data, 'finger1');
    if (finger1.split('|').length === 1) {
      const finger2 = SimcInput.find(data, 'finger2');
      data = SimcInput.set(data, 'finger1', [finger1, finger2].join('|'));
    }
    const trinket1 = SimcInput.find(data, 'trinket1');
    if (trinket1.split('|').length === 1) {
      const trinket2 = SimcInput.find(data, 'trinket2');
      data = SimcInput.set(data, 'trinket1', [trinket1, trinket2].join('|'));
    }
    data = SimcInput.reject(data, 'finger2');
    data = SimcInput.reject(data, 'trinket2');
  }
  return data;
}

function parseItemAttributes(item) {
  const rawAttrs = item.split(',');
  const attributes = _.fromPairs(_.tail(rawAttrs).map(attr => attr.split('=')));
  return attributes;
}

const SOURCE_MAP = {
  gear_from_bags: 'bag',
  weekly_reward_choices: 'weekly_reward',
  linked_gear: 'item_link',
};

export function makeGearOptions(data) {
  if (!data || data.length === 0) {
    return {};
  }
  const options = {};
  // simpermut has the same data in finger1 and finger2. same for trinkets
  const slots = [
    'head', 'neck', 'shoulder', 'back', 'chest', 'wrist', 'hands', 'waist', 'legs', 'feet',
    'finger1', 'finger2', 'trinket1', 'trinket2', 'main_hand', 'off_hand',
    'unknown',
  ];

  _.each(slots, slot => {
    options[slot] = [];
    const line = SimcInput.find(data, slot);
    if (line) {
      // handles both normal simc equipped gear input and all simpermut gear input
      const items = line.split('|');
      _.each(items, item => {
        const attributes = parseItemAttributes(item);
        // for simpermut, treat everything as equipped to ensure all items are checked on init
        attributes.equipped = true;
        options[slot].push(attributes);
      });
    }
  });

  // find items in comments
  let source;
  _.each(
    _.filter(data, line => line.comment),
    line => {
      const trimmed = _.trim(line.comment);
      // try to parse
      const [command, ...rest] = trimmed.split('=');
      const slot = COMMANDS_GEAR_ALIASES[command] || command;
      if (_.includes(slots, slot)) {
        const rawAttrs = rest.join('=');
        const attrs = parseItemAttributes(rawAttrs);
        attrs.source = source;
        options[slot].push(attrs);
      } else if (_.startsWith(trimmed, '##')) {
        // section delimeter
        source = SOURCE_MAP[_.snakeCase(_.trim(trimmed, '# '))] || 'unknown';
      }
    },
  );

  // merge rings and trinkets

  options.rings = _.union(options.finger1, options.finger2);
  delete options.finger1;
  delete options.finger2;

  options.trinkets = _.union(options.trinket1, options.trinket2);
  delete options.trinket1;
  delete options.trinket2;

  return options;
}

const slotHasAzeritePiece = items => {
  const numAzeritePieces = _.size(_.filter(items, i => i.azeritePowerSetId));
  return numAzeritePieces > 0;
};

export function isGearValidForOptimize(gear) {
  const inclusiveMinimums = {
    head: 1,
    neck: 1,
    shoulder: 1,
    back: 1,
    chest: 1,
    wrist: 1,
    hands: 1,
    waist: 1,
    legs: 1,
    feet: 1,
    main_hand: 1,
    rings: 2,
    trinkets: 2,
  };

  const valid = _.reduce(
    _.mapValues(inclusiveMinimums, (value, slot) => {
      const result = gear[slot] && (gear[slot].length >= value || slotHasAzeritePiece(gear[slot]));
      return result;
    }),
    (result, aboveMin) => result && aboveMin,
    true,
  );

  return valid;
}

export function getSelectedGear(
  gear,
  selected,
  selectedAzeritePowers,
) {
  // expand azerite and do some other baseline work of populating indexes
  const result = _.mapValues(gear, (items, slot) => {
    const slotSelected = _.filter(
      // save original index
      _.map(items, (item, index) => {
        return {
          ...item,
          origIdx: index,
          selectedIdx: index,
        };
      }),
      // filter selected items
      (item, index) => _.includes(selected[slot], index),
    );

    // expand/replace azerite gear with power combinations
    const slotItems = [];

    _.each(slotSelected, item => {
      const azeritePowers = _.get(_.find(selectedAzeritePowers[slot], { idx: item.origIdx }), 'powers');

      // if no powers selected or a normal item
      if (!azeritePowers || azeritePowers.length === 0) {
        // push the base item
        slotItems.push(item);
      } else {
        // transform az powers back into tiered array for combo making
        const powers = _.map(azeritePowers, power => _.find(item.azeritePowers, { powerId: power }));
        const powersByTier = _.toArray(_.groupBy(powers, 'tier'));
        const powerCombos = Combinatorics.cartesianProduct(...powersByTier).toArray();

        let equippedPowers = [];
        if (item.azerite_powers) {
          equippedPowers = new Set(_.map(item.azerite_powers.split('/'), p => parseInt(p, 10)));
        }

        _.each(powerCombos, powerCombo => {
          const powerList = _.map(_.reverse(powerCombo), 'powerId');
          const newIdx = slotItems.length;
          slotItems.push({
            ...item,
            // make sure we save the new idx for this combo
            origIdx: newIdx,
            equipped: item.equipped && _.isEqual(equippedPowers, new Set(powerList)),
            azerite_powers: powerList.join('/'),
          });
        });
      }
    });
    return slotItems;
  });

  return result;
}

export function getTalentCombinations(selectedTalents) {
  let selected = selectedTalents;
  if (_.isEmpty(selected) || !_.isArray(selected)) {
    selected = [
      [false, false, false],
      [false, false, false],
      [false, false, false],
      [false, false, false],
      [false, false, false],
      [false, false, false],
      [false, false, false],
    ];
  }
  const talentOptions = _.map(
    selected,
    cols => {
      const trimmed = _.reject(
        _.map(cols, (t, idx) => t && idx),
        t => t === false,
      );
      if (trimmed.length === 0) {
        return ['.'];
      }
      return trimmed;
    },
  );

  const talentCombos = _.map(
    Combinatorics.cartesianProduct(...talentOptions).toArray(),
    arr => talentsArmoryToSimc(arr.join('')),
  );

  return talentCombos;
}

function getGemCombinationsDirect(gems, forceMainStat, socketLayout) {
  if (socketLayout.length === 0) {
    return [];
  }

  const socketType = socketLayout[0];
  let validGems = gems[socketType];

  if (_.size(validGems) === 0) {
    validGems = [{
      enhancementId: null,
      type: socketType,
      gem: { name: 'EMPTY' },
    }];
  }

  if (socketLayout.length === 1) {
    return _.map(validGems, g => [{ gem: g.enhancementId, type: socketType, gemDetail: g }]);
  }

  const results = [];

  for (let i = 0; i < validGems.length; i += 1) {
    const fixedGem = validGems[i];

    let nextGems = _.mapValues(gems, (typeGems, type) => {
      if (type === socketType) {
        return _.drop(typeGems, i);
      }

      return typeGems;
    });

    if (forceMainStat && !fixedGem.unique) {
      // eslint-disable-next-line no-continue
      continue;
    }

    // drop from next slot recusive call
    if (fixedGem.unique) {
      nextGems = _.mapValues(nextGems, (typeGems, type) => {
        if (type === socketType) {
          return _.reject(typeGems, { unique: fixedGem.unique });
        }
        return typeGems;
      });
    }

    const subCombos = getGemCombinationsDirect(nextGems, false, socketLayout.slice(1));
    _.each(subCombos, combo => {
      results.push([
        {
          gem: fixedGem.enhancementId,
          type: socketType,
          gemDetail: fixedGem,
        },
        ...combo,
      ]);
    });
  }

  return results;
}

// filter out non-optimal domination socket layouts:
//  3+ sockets: fulfill set bonus
//  X > 3 sockets: (X-2) dps gems
const isOptimalDominationLayout = (layout, shardsSelected, dpsShardsSelected, canGetSetBonus) => {
  let numSockets = 0;
  let numEquipped = 0;
  let numDpsShards = 0;
  let fulfillSetBonus = false;
  const dominationTypes = {};
  for (let i = 0; i < layout.length; i += 1) {
    const gem = layout[i];
    if (gem.type === 'DOMINATION') {
      numSockets += 1;
      if (gem.equipped) {
        numEquipped += 1;
      }
      if (gem.gemDetail.gem.dps) {
        numDpsShards += 1;
      }
      const group = gem.gemDetail.gem.group;
      if (!dominationTypes[group]) {
        dominationTypes[group] = 0;
      }
      dominationTypes[group] += 1;
      if (dominationTypes[group] >= 3) {
        fulfillSetBonus = true;
      }
    }
  }

  // this is already in the gear, allow this combo
  // otherwise everything breaks if the player has a non-optimal combo in their gear
  if (numSockets === numEquipped) {
    return true;
  }

  // if there are fewer shards selected than sockets available, treat that number
  // as the number of sockets to avoid null combination issues
  numSockets = _.min([numSockets, shardsSelected]);
  let minDpsGems = _.min([dpsShardsSelected, numSockets]);
  if (canGetSetBonus && numSockets >= 3) {
    minDpsGems = _.min([dpsShardsSelected, numSockets - 2]);
  }

  // accept all variations when the character has 0, 1, or 2 domination sockets
  if (numSockets < 3) {
    return numDpsShards >= minDpsGems;
  }
  // For 3, 4 sockets, look at set bonus or more than 1 dps shard
  if (numSockets < 5) {
    if (canGetSetBonus) {
      return fulfillSetBonus && numDpsShards >= minDpsGems;
    }
    return numDpsShards >= minDpsGems;
  }

  // otherwise, only accept set bonus + 3 dps shards
  return (!canGetSetBonus || fulfillSetBonus) && numDpsShards >= minDpsGems;
};

// good god, so many dragons
export function getGemCombinations(
  selectedGems,
  forceMainStat,
  socketLayout,
  filledSockets,
  specInfo,
  enableRuneWords,
) {
  // demo locks and maybe monks might consider Jas to be a DPS gem
  // needs to be done here because multiple places look at the DPS property so we just want to
  // do the determination once
  const specScalesWithHp = _.includes(specInfo.extraStatScales, 'stamina');

  const gems = _.reverse(
    _.sortBy(
      _.map(
        selectedGems,
        gem => {
          // do some data hacking of the shards to make Jas look like a DPS shard for specs that care
          if (specScalesWithHp && gem.gem.unique === 'dominationJas') {
            return {
              ...gem,
              gem: {
                ...gem.gem,
                dps: true,
              },
            };
          }
          return gem;
        },
      ),
      'unique',
    ),
  );

  const gemsByType = _.groupBy(gems, 'gem.type');

  // add a check to make sure that a main stat gem is actually selected before applying forceMainStat
  const forceMain = forceMainStat && Boolean(_.find(gems, g => g.unique));

  const validGemSets = getGemCombinationsDirect(gemsByType, forceMain, socketLayout);

  // find minimum number of empty sockets
  const gemSetsByEmptySockets = _.groupBy(
    validGemSets,
    vgs => _.reduce(vgs, (empties, gem) => (gem.gem === null ? empties + 1 : empties), 0),
  );

  // only keep gem combos with min number of empty sockets
  const leastEmpties = gemSetsByEmptySockets[_.min(_.keys(gemSetsByEmptySockets))];

  let result = _.sortBy(leastEmpties, 'type');

  // Fuck domination shards and fuck my socket code

  // If "Replace Existing" is not chosen, we need to look at the already filled dom shards
  // to determine if a non-dps shard is desirable because it would complete a set bonus.
  // So we fake up the selected gems with the already filled dom shards and calculate from there
  const extantDomGems = _.filter(_.map(filledSockets, socket => {
    if (_.size(socket.socketInfo?.gems) === 0) {
      return null;
    }
    const gem = socket.socketInfo.gems[0];
    return {
      gem: gem.id,
      type: socket.type,
      gemDetail: {
        enhancementId: null,
        slot: 'socket',
        specId: null,
        unique: gem.unique,
        gem,
      },
      equipped: true,
    };
  }));

  const selectedAndFilledDomGems = [
    ...(gemsByType.DOMINATION || []),
    ..._.map(extantDomGems, 'gemDetail'),
  ];
  const domGems = _.size(_.groupBy(selectedAndFilledDomGems, 'unique'));
  const dpsDomGems = _.min([
    _.size(
      _.groupBy(
        _.filter(
          selectedAndFilledDomGems,
          'gem.dps',
        ),
        'unique',
      ),
    ),
    3,
  ]);
  const maxDomColorGems = _.max(
    _.map(
      _.groupBy(
        _.uniqBy(selectedAndFilledDomGems, 'unique'),
        'gem.group',
      ),
      g => _.size(g),
    ),
  );
  const canGetSetBonus = maxDomColorGems === 3 && enableRuneWords;

  const layoutKeys = new Set();
  result = _.filter(result, layout => {
    let fullLayout = layout;
    if (_.size(filledSockets) > 0) {
      fullLayout = [
        ...extantDomGems,
        ...layout,
      ];
    }
    if (!isOptimalDominationLayout(fullLayout, domGems, dpsDomGems, canGetSetBonus)) {
      return false;
    }

    // dedupe utility shards
    const layoutKey = _.map(
      _.sortBy(layout, ['gemDetail.gem.dps', 'type', 'gem']),
      'gem',
    ).join('/');
    if (layoutKeys.has(layoutKey)) {
      return false;
    }
    layoutKeys.add(layoutKey);

    return true;
  });

  return result;
}

export function getEssenceCombinations(selectedEssences, passiveEssences) {
  let essenceCombos;
  const essenceSlotsOptions = _.groupBy(selectedEssences, 'slot');

  if (_.size(selectedEssences) > 0) {
    const rawEssenceCombos = Combinatorics.cartesianProduct(..._.values(essenceSlotsOptions));

    // locking slot 1 is handled upstream - frontend just populates slot 1 with the locked essence so this code
    // doesn't have to think about it

    // check for invalid essence configs:
    //  * Essence used multiple times
    const minorCombinations = new Set();
    const activeCombos = rawEssenceCombos.filter(combo => {
      // check for dupes
      const seen = new Set();
      for (let i = 0; i < combo.length; i += 1) {
        if (seen.has(combo[i].essenceId)) {
          return false;
        }
        seen.add(combo[i].essenceId);
      }
      // grab and sort slot 1 and 2 and reject if they are already seen
      if (combo.length > 2) {
        const minorKey = [
          combo[0].essenceId,
          ..._.map(_.reject(combo, { slot: 0 }), 'essenceId').sort(),
        ].join('/');
        if (minorCombinations.has(minorKey)) {
          return false;
        }
        minorCombinations.add(minorKey);
      }
      return true;
    });

    essenceCombos = _.map(activeCombos, combo => {
      const essenceStr = stringifyAzeriteEssences(parseAzeriteEssences(_.union(
        _.map(combo, c => `${c.essenceId}:${c.rank}`),
        passiveEssences,
      ).join('/')));
      return {
        corruptionReduction: 0,
        str: essenceStr,
      };
    });
  }
  return essenceCombos;
}


// TODO: Lot of room for optimization here if needed
const getSoulbindCombos = (
  soulbinds,
  soulbindGroups,
  conduits,
  conduitRankOverride,
  unlockedTraits,
  allSoulbinds,
  renown,
  includeEnhancedPermutations = true,
) => {
  const combos = [];

  const comboConduitMap = {
    endurance: 0,
    finesse: 1,
    potency: 2,
  };

  // decorate conduits with full data
  const conduitsDecorated = _.map(conduits, conduit => {
    return {
      ..._.find(GameData.Conduits, { id: conduit.id }),
      ...conduit,
    };
  });

  const groups = _.groupBy(soulbindGroups, 'soulbindId');

  // iterate over each soulbind
  _.each(soulbinds, soulbindId => {
    const soulbind = getSoulbind(soulbindId, renown);
    const group = groups[soulbindId];
    const byRow = [];
    const covenant = getCovenant(soulbind.covenantId);

    // group choices by row
    _.each(group, g => {
      if (!byRow[g.row]) {
        byRow[g.row] = [];
      }
      byRow[g.row].push({
        ...g,
        talents: _.filter(soulbind.talents, talent => {
          const unlockKey = _.join([soulbind.id, talent.tier], ':');
          // if unlockedTraits is not set, that means an older version of the frontend code
          // that should skip this check
          if (allSoulbinds || !unlockedTraits || _.includes(unlockedTraits, unlockKey)) {
            return _.isEqual(talent.choiceIndex, [g.row, g.col]);
          }
          return false;
        }),
      });
    });

    // no path selected
    if (byRow.length === 0) {
      return;
    }

    // clean up rows that are not chosen
    // TODO: better handling of this? UI disallow unselecting items if there's a next row selected?
    const rows = _.filter(byRow);

    // determine the possible paths without conduits
    const basePaths = Combinatorics.cartesianProduct(...rows).toArray();

    _.each(basePaths, path => {
      // count up the conduit slots in this path
      const conduitSlots = _.reduce(path, (result, choice) => {
        _.each(choice.talents, talent => {
          if (talent.conduitType) {
            // eslint-disable-next-line no-param-reassign
            result[talent.conduitType].total += 1;
            if (renown >= talent.enhancedRenownLevel) {
              // eslint-disable-next-line no-param-reassign
              result[talent.conduitType].enhanced += 1;
            }
          }
        });
        return result;
      }, {
        endurance: {
          total: 0,
          enhanced: 0,
        },
        finesse: {
          total: 0,
          enhanced: 0,
        },
        potency: {
          total: 0,
          enhanced: 0,
        },
      });

      // determine valid conduit combinations for this slot layout
      const conduitCombos = _.mapValues(conduitSlots, (slotInfo, slot) => {
        const num = slotInfo.total;
        const numEnhanced = slotInfo.enhanced;
        const matchingConduits = _.filter(conduitsDecorated, conduit => {
          if (conduit.type !== slot) {
            return false;
          }
          if (conduit.covenant && conduit.covenant !== covenant.id) {
            return false;
          }
          return true;
        });
        if (matchingConduits.length > 0 && num > 0) {
          // if we have fewer conduits chosen, use that size for combination size, not total number of slots
          let cc;
          if (includeEnhancedPermutations && slot === 'potency' && numEnhanced && num !== numEnhanced) {
            // order matters for potency if there are partial enhanced sockets
            cc = Combinatorics.permutation(matchingConduits, _.min([matchingConduits.length, num])).toArray();
            // TODO: basic permutation can result in duplicate variations, do some dedupe for those
            // e.g., enhanced-normal-normal slots, A-B-C and A-C-B will not DPS differ
          } else {
            // order does not matter if there are no enhanced sockets or if all sockets are enhanced
            cc = Combinatorics.combination(matchingConduits, _.min([matchingConduits.length, num])).toArray();
          }
          return cc;
        }
        return [null];
      });

      // create all conduit variations for this path
      const conduitLayouts = Combinatorics.cartesianProduct(..._.values(conduitCombos)).toArray();

      _.each(conduitLayouts, layout => {
        // keep track of which next conduit to slot in
        const conduitIndex = {
          endurance: 0,
          finesse: 0,
          potency: 0,
        };
        const soulbindParts = [];
        const pathWithConduits = _.map(path, choice => {
          // there could be multiple conduits in this path
          const pathConduits = [];
          _.each(_.filter(_.map(choice.talents, 'conduitType')), conduitType => {
            if (conduitType) {
              const conduitsToUse = layout[comboConduitMap[conduitType]];
              if (conduitsToUse) {
                const conduit = conduitsToUse[conduitIndex[conduitType]];
                conduitIndex[conduitType] += 1;
                if (conduit) {
                  pathConduits.push(conduit);
                }
              }
            }
          });
          return {
            ...choice,
            conduits: pathConduits,
          };
        });
        _.each(pathWithConduits, choice => {
          _.each(choice.talents, talent => {
            if (talent.conduitType) {
              const conduitToUse = _.find(choice.conduits, { type: talent.conduitType });
              if (conduitToUse) {
                const rank = conduitRankOverride || conduitToUse.rank;
                soulbindParts.push(`${conduitToUse.id}:${rank}:${talent.enhanced ? 1 : 0}`);
              } else {
                soulbindParts.push('');
              }
            } else {
              soulbindParts.push(`${talent.spellId}`);
            }
          });
        });
        const key = _.join([soulbind.key, soulbind.id], ':');
        const soulbindStr = _.join([key, _.join(_.filter(soulbindParts), '/')], ',');
        combos.push({
          str: soulbindStr,
          path: pathWithConduits,
          covenant: getCovenant(soulbind.covenantId),
        });
      });
    });
  });

  return combos.length > 0 ? combos : null;
};

const getParsed = item => {
  if (item) {
    const gems = _.filter(
      _.map(
        _.split(item.gem_id, '/'),
        gid => {
          if (gid) {
            const gem = _.find(gemInfo, { id: parseInt(gid) });
            return gem;
          }
          return null;
        },
      ),
    );

    let enchant;
    let enchantId;
    if (item.enchant_id) {
      enchantId = parseInt(item.enchant_id, 10);
      enchant = getEnchant(enchantId);
    }

    return {
      azerite_powers: _.filter(_.map(_.split(item.azerite_powers, '/'), p => parseInt(p, 10))),
      enchant_id: enchantId,
      enchant,
      gems,
      uniqueGems: _.filter(gems, 'unique'), // denormalized for lookup speed later
    };
  }
  return undefined;
};
const makeNewCacheItem = item => {
  return {
    ...item,
    cacheGuid: nanoid(),
  };
};
const decorateItem = item => {
  if (item) {
    return {
      ...item,
      parsed: getParsed(item),
      socketInfo: itemSocketInfo(item),
    };
  }
  return null;
};

export function getRawCombinations(
  gear,
  gem,
  cloakEnchant,
  ringEnchant,
  neckEnchant,
  weaponEnchant,
  weaponOffhandEnchant,
  replaceExisting,
  classId,
  specName,
  selectedTalents,
  lockedTalents,
  selectedEnhancements,
  enhancementReplace,
  selectedEssences,
  passiveEssences,
  addSocketNum,
  addSocketGem,
  soulbinds,
  soulbindGroups,
  conduits,
  conduitRankOverride,
  unlockedSoulbindTraits,
  allSoulbinds,
  renown,
  soulbindsModified,
) {
  const slots = [
    'head', 'neck', 'shoulder', 'back', 'chest', 'wrist', 'hands', 'waist',
    'legs', 'feet', 'rings', 'trinkets', 'main_hand', 'off_hand',
  ];
  if (!gear) {
    return [];
  }
  const requiredSlots = _.pull(slots, 'off_hand');
  const gearPopulated = _.reduce(requiredSlots, (res, slot) => res && gear[slot] && gear[slot].length > 0, true);
  if (!gearPopulated) {
    return [];
  }

  const specDetails = getSpecInfo(classId, specName) || {};

  const enhancementsBySlot = _.groupBy(_.filter(selectedEnhancements, { specId: specDetails.id }), 'slot');

  // update socket info for later
  let trimmedGear = _.mapValues(gear, items => _.map(items, item => {
    const customItemLimits = [];
    // only allow a single weekly reward item in a combination
    if (item.source === 'weekly_reward') {
      customItemLimits.push({
        category: 'weekly_reward',
        quantity: 1,
      });
    }

    return {
      ...decorateItem(_.omit(item, [
        'chanceBonusList',
        'names',
        'stats',
        'bonusLists',
      ])),
      // assign a unique guid if item doesn't already have one (weapons will have guids)
      // This will be checked later for ring/trinket combos to make sure we don't re-use the same item twice with
      // different gem/enchants
      guid: item.guid || nanoid(),
      cacheGuid: nanoid(),
      socketInfo: itemSocketInfo(item),
      canRollSocket: canRollSocket(item, SOCKET_TYPES.PRISMATIC),
      customItemLimits,
    };
  }));

  const enchantSlotMap = {
    rings: 'finger',
  };

  // setup enchant overrides
  trimmedGear = _.mapValues(trimmedGear, (items, slot) => {
    const enchantSlot = enchantSlotMap[slot] || slot;
    if (enhancementsBySlot[enchantSlot]) {
      const slotItems = [];
      _.each(items, item => {
        if (enhancementReplace || !item.enchant_id) {
          _.each(enhancementsBySlot[enchantSlot], enchant => {
            // don't use makeNewCacheItem here, this is a modification of an existing item
            const newItem = decorateItem({
              ...item,
              enchant_id: enchant.enhancementId,
              override: {
                enchant_id: enchant.enhancementId,
              },
            });
            slotItems.push(newItem);
          });
        } else {
          slotItems.push(item);
        }
      });
      return slotItems;
    }
    return items;
  });

  // add prismatic sockets
  const addSocketGemId = _.get(_.find(addSocketGem, { specId: specDetails.id }), 'gemId');
  if (addSocketNum && addSocketGemId) {
    trimmedGear = _.mapValues(trimmedGear, (items) => {
      const slotItems = [];
      _.each(items, item => {
        slotItems.push(item);
        if (canRollSocket(item, SOCKET_TYPES.PRISMATIC)) {
          const newItem = makeNewCacheItem(item);
          const gemId = addSocketGemId;
          const newBonusIds = _.split(item.bonus_id, '/');
          newBonusIds.push(1808);
          newItem.bonus_id = newBonusIds.join('/');
          newItem.equipped = false;
          newItem.hasSockets = true;
          newItem.gem_id = gemId;
          newItem.override = {
            ...newItem.override,
            gem_id: gemId,
            addSocket: true,
          };
          // fake an item limit category
          newItem.customItemLimits = [
            ...newItem.customItemLimits,
            {
              category: 'addSocket',
              quantity: addSocketNum,
            },
          ];
          slotItems.push(decorateItem(newItem));
        }
      });
      return slotItems;
    });
  }

  if (!trimmedGear.rings || trimmedGear.rings.length < 2) {
    return [];
  }
  if (!trimmedGear.trinkets || trimmedGear.trinkets.length < 2) {
    return [];
  }

  // figure out valid weapon combos

  if (!trimmedGear.main_hand) {
    return [];
  }


  //-----------------------------------
  // weapon permutations

  const specCanDualWield = specDetails.dualWield;
  const specTwoHandersInOneHand = specDetails.dualWield2h;

  const rawWeaponProduct = Combinatorics.cartesianProduct(trimmedGear.main_hand, [...trimmedGear.off_hand, null]);
  // filter raw weapon product to usable combos

  const weaponPermutations = _.filter(rawWeaponProduct.toArray(), weaponSet => {
    const [mainHand, offHand] = weaponSet;
    const mainHandInfo = getWeaponHands(mainHand);

    // don't re-use the same weapon
    if (offHand && mainHand.guid && offHand.guid && mainHand.guid === offHand.guid) {
      return false;
    }

    // dual wielders should use two weapons
    if (mainHandInfo !== '2h' && specCanDualWield && offHand === null) {
      return false;
    }

    // remove 2h + 1h combos for classes that can't use
    if (mainHandInfo === '2h' && offHand !== null && !specTwoHandersInOneHand) {
      return false;
    }

    // remove empty off hand for fury warriors
    if (specTwoHandersInOneHand && offHand === null) {
      return false;
    }

    // fill both slots
    if (_.includes(['mh', '1h'], mainHandInfo) && offHand === null) {
      return false;
    }

    // reject artifact/non-artifact in same combo
    if (mainHand && offHand) {
      if (mainHand.quality === 6 || offHand.quality === 6) {
        if (mainHand.quality !== offHand.quality) {
          return false;
        }
      }
    }

    return true;
  });

  if (weaponPermutations.length === 0) {
    return [];
  }

  //-----------------------------------
  // generate talent combinations

  // TODO: shift talent combos to getCombinations to avoid reprocessing a bunch of gem filling for better perf

  let talentCombos = null;
  if (selectedTalents && lockedTalents) {
    talentCombos = _.union(
      getTalentCombinations(selectedTalents),
      lockedTalents,
    );
  }

  //-----------------------------------
  // generate azerite essence combinations

  // TODO: shift azerite essence combos to getCombinations to avoid reprocessing a bunch of gem filling for better perf

  const essenceCombos = getEssenceCombinations(selectedEssences, passiveEssences);

  if (essenceCombos && essenceCombos.length === 0) {
    return [];
  }

  //-----------------------------------
  // generate soulbind combos

  const soulbindCombos = getSoulbindCombos(
    soulbinds,
    soulbindGroups,
    conduits,
    conduitRankOverride,
    unlockedSoulbindTraits,
    allSoulbinds,
    renown,
    soulbindsModified,
  );

  //-----------------------------------
  // create the raw cartesian product

  try {
    // get ring and trinket combos, filter out items that got duped because of gem/enchant shenanigans
    const ringCombos = _.reject(
      Combinatorics.bigCombination(trimmedGear.rings, 2).toArray(),
      ([ring1, ring2]) => ring1.guid === ring2.guid,
    );
    if (ringCombos.length === 0) {
      return [];
    }
    const allTrinketsEquipped = _.every(trimmedGear.trinkets, { equipped: true });
    const trinketCombos = _.reduce(
      _.reject(
        Combinatorics.bigCombination(trimmedGear.trinkets, 2).toArray(),
        ([trinket1, trinket2]) => trinket1.guid === trinket2.guid,
      ),
      (result, [trinket1, trinket2]) => {
        result.push([trinket1, trinket2]);
        // make sure double 'on use' trinkets are tested in both slots. First trinket will get more bloodlust time,
        // variations might correspond better with spec cooldowns, etc
        if (!allTrinketsEquipped && trinket1.onUseTrinket && trinket2.onUseTrinket) {
          result.push([{
            ...trinket2,
            equipped: false,
          }, {
            ...trinket1,
            equipped: false,
          }]);
        }
        return result;
      },
      [],
    );

    if (trinketCombos.length === 0) {
      return [];
    }

    const cpArgs = [
      trimmedGear.head,
      trimmedGear.neck,
      trimmedGear.shoulder,
      trimmedGear.back,
      trimmedGear.chest,
      trimmedGear.wrist,
      trimmedGear.hands,
      trimmedGear.waist,
      trimmedGear.legs,
      trimmedGear.feet,
      ringCombos,
      trinketCombos,
      weaponPermutations,
    ];

    if (talentCombos) {
      cpArgs.push(talentCombos);
    }

    if (essenceCombos) {
      cpArgs.push(essenceCombos);
    }

    if (soulbindCombos) {
      cpArgs.push(soulbindCombos);
    }

    /*
    if (cp.length > SharedConfig.optimize.maxRawCombinations) {
      console.log('too many raw combos', cp.length, SharedConfig.optimize.maxRawCombinations);
      return null;
    }
    */

    /*
     * Depth first search of possible gearsets
     * Try to prune branches as quickly as possible rather than doing a full cartesian product and then iterating
     * over everything and filtering things out after-the-fact
     *
     * Takes in a strategy that sorts the slots to experiment to find the best slot order to search.
     * Pruning checks performned:
     *  Reject sets with two unique items
     *  Reject sets that violate category item limits
     *  Reject sets with two unique gems
    */
    let walkCalls = 0;
    let partialsExplored = 0;
    let leavesExplored = 0;
    let tooLarge = false;
    // eslint-disable-next-line no-unused-vars
    let rejections;
    // eslint-disable-next-line no-unused-vars
    let rejectedAt;
    const getCombos = (rawData, {
      sortStrategy = null,
    }) => {
      // TODO: Figure out if there's some smart way to estimate the size here
      const combinations = new Array(10000);
      let nextCombo = 0;
      let calls = 0;
      let partials = 0;
      let leaves = 0;
      const internalRejections = {
        customItemLimits: 0,
        uniqueEquipped: 0,
        itemLimits: 0,
        uniqueGems: 0,
      };
      const internalRejectedAt = _.map(_.range(0, 15), () => 0);

      const maxLen = rawData.length;

      let data;
      if (sortStrategy) {
        const withOrigIdx = _.map(rawData, (el, idx) => ({ data: el, origIdx: idx }));
        data = sortStrategy(withOrigIdx);
      } else {
        data = rawData;
      }

      /*
       * WARNING!
       *
       * This is a heavily optimized piece of code that is easy to screw up.
       * Don't be an idiot, be careful with it.
       */

      const walk = (
        left,
        candidate = [],
        candidateLen = 0,
        uniqueIds = new Set(),
        itemLimitCounts = new Map(),
        uniqueGems = new Set(),
        covenant = null,
      ) => {
        calls += 1;
        if (
          tooLarge
          || nextCombo > SharedConfig.optimize.maxRawCombinations
          || partials > SharedConfig.optimize.maxWalkPartials
        ) {
          tooLarge = true;
        } else if (candidateLen === maxLen) {
          leaves += 1;
          // Only add sets that are fully socketed or have added a socket
          if (addSocketNum && addSocketGemId) {
            let hasAdded = false;
            let canSocket = 0;
            let socketed = 0;
            for (let i = 0; i < candidateLen; i += 1) {
              const it = candidate[i];
              if (it.override && it.override.addSocket) {
                hasAdded = true;
              }
              if (it.canRollSocket) {
                canSocket += 1;
              }
              if (it.socketInfo?.PRISMATIC?.total > 0) {
                socketed += 1;
              }
            }
            if (socketed < canSocket && !hasAdded) {
              return;
            }
          }
          combinations[nextCombo] = candidate;
          nextCombo += 1;
        } else {
          const newLeft = left.slice(1);

          for (let i = 0; i < left[0].data.length; i += 1) {
            partials += 1;
            const newItem = left[0].data[i];

            // Warning, heavy optimization ahead!
            // Most of these are empty placeholder, we only copy if we need to make a change
            // This should reduce a huge amount of object copy churn for no real need
            let nextItemLimitCounts;
            let nextUniqueIds;
            let nextUniqueGems;
            let nextCovenant;

            if (newItem) {
              // convert to array
              let items;
              if (newItem.length) {
                items = newItem;
              } else {
                items = [newItem];
              }

              let reject = false;

              // rings and trinkets will be two-fer
              for (let j = 0; j < items.length; j += 1) {
                const item = items[j];

                if (!item) {
                  // eslint-disable-next-line no-continue
                  continue;
                }

                /*
                 * Check on various validity constraints. Order here could be very important
                 * as this is a very tight loop that might be executed millions of times so
                 * the first checks should do as much as possible.
                 *
                 * TODO: dynamic ordering of checks based on previous rejections?
                 */

                // this is a soulbind entry
                if (item.covenant && item.path) {
                  nextCovenant = item.covenant.id;
                }

                //////////////////////////////////////////////
                // custom item limit checks
                //////////////////////////////////////////////

                // item limits for things like add socket and preserved contaminant
                if (item.customItemLimits) {
                  // make sure we only create once in case of ring/trinket loop
                  if (!nextItemLimitCounts) {
                    nextItemLimitCounts = new Map(itemLimitCounts);
                  }
                  // It's ok to ignore this, reject is checked outside the loop
                  // eslint-disable-next-line no-loop-func
                  for (let limitIdx = 0; limitIdx < item.customItemLimits.length; limitIdx += 1) {
                    const { category, quantity } = item.customItemLimits[limitIdx];
                    const newValue = 1 + (nextItemLimitCounts.get(category) || 0);
                    if (newValue > quantity) {
                      reject = true;
                      internalRejections.customItemLimits += 1;
                      break;
                    }
                    nextItemLimitCounts.set(category, newValue);
                  }
                  if (reject) {
                    break;
                  }
                }


                //////////////////////////////////////////////
                // unique equipped check
                //////////////////////////////////////////////

                // unique equipped
                if (item.uniqueEquipped) {
                  // make sure we only create once in case of ring/trinket loop
                  if (!nextUniqueIds) {
                    nextUniqueIds = new Set(uniqueIds);
                  }
                  if (nextUniqueIds.has(item.id)) {
                    reject = true;
                    internalRejections.uniqueEquipped += 1;
                    break;
                  } else {
                    nextUniqueIds.add(item.id);
                  }
                }


                //////////////////////////////////////////////
                // item limit check
                //////////////////////////////////////////////

                // item limits
                if (item.itemLimit) {
                  // make sure we only create once in case of ring/trinket loop
                  if (!nextItemLimitCounts) {
                    nextItemLimitCounts = new Map(itemLimitCounts);
                  }

                  const category = item.itemLimit.category;
                  const newValue = 1 + (nextItemLimitCounts.get(category) || 0);

                  if (newValue > item.itemLimit.quantity) {
                    reject = true;
                    internalRejections.itemLimits += 1;
                    break;
                  }
                  nextItemLimitCounts.set(category, newValue);
                }


                //////////////////////////////////////////////
                // unique gem check
                //////////////////////////////////////////////

                // unique gems
                // Category uniqueness is manually entered in items.js with either an itemLimitCategory
                // or a fake string name for the gem uniqueness group (Domination gems)
                // TODO: The uniqueness code might break for an item with 2 sockets of the same type? Maybe not
                // eslint-disable-next-line no-loop-func
                _.each(item.socketInfo, (socketInfo, socketType) => {
                  if (socketInfo.total > 0) {
                    if (item.parsed.uniqueGems.length > 0) {
                      if (!nextUniqueGems) {
                        nextUniqueGems = new Set(uniqueGems);
                      }
                      for (let g = 0; g < item.parsed.uniqueGems.length; g += 1) {
                        const uniqueGem = item.parsed.uniqueGems[g];
                        if (uniqueGem.type === socketType) {
                          if (nextUniqueGems.has(uniqueGem.unique)) {
                            reject = true;
                            internalRejections.uniqueGems += 1;
                            return false;
                          }
                          nextUniqueGems.add(uniqueGem.unique);
                        }
                      }
                    }
                  }
                  return true;
                });
                if (reject) {
                  break;
                }

                ///////////////////////////////////////////////////
                // Covenant-specific item check (9.1 legendaries)
                ///////////////////////////////////////////////////

                if (item.requiredCovenant && covenant && item.requiredCovenant !== covenant) {
                  reject = true;
                  break;
                }
              }
              if (reject) {
                leaves += 1;
                internalRejectedAt[candidateLen] += 1;
                // eslint-disable-next-line no-continue
                continue;
              }
            }

            const newCandidate = candidate.slice();
            newCandidate[left[0].origIdx] = newItem;

            walk(
              newLeft,
              newCandidate,
              candidateLen + 1,
              // the "next" copies may have never been instantiated if they were never needed
              // in that case, pass along the original
              nextUniqueIds || uniqueIds,
              nextItemLimitCounts || itemLimitCounts,
              nextUniqueGems || uniqueGems,
              nextCovenant || covenant,
            );
          }
        }
      };

      walk(data);
      walkCalls = calls;
      partialsExplored = partials;
      leavesExplored = leaves;
      // eslint-disable-next-line no-unused-vars
      rejections = internalRejections;
      // eslint-disable-next-line no-unused-vars
      rejectedAt = internalRejectedAt;

      //devTable(rejectedAt);
      //devTable(rejections);

      return combinations.slice(0, nextCombo);
    };

    // default strategy - fewest slot items first
    const strategies = ['soulbinds-then-size-asc'];

    /*
    const testStrategies = [
      'size-asc',
      'soulbinds-then-size-asc',
    ];

    if (DEVMODE) {
      strategies = testStrategies;
    }
    // */

    let combinations;

    const results = [];
    _.each(strategies, strategyName => {
      let strategy;
      switch (strategyName) {
        case 'size-asc':
          strategy = (rawData) => _.sortBy(rawData, items => items.data.length);
          break;
        // soulbinds/covenants are iterated first
        // This makes sure we know the covenant ASAP so we can reject any potential combo that has items for
        // another covenant (like a 9.1 legendary)
        case 'soulbinds-then-size-asc':
          strategy = (rawData) => _.sortBy(rawData, items => {
            if (items.origIdx === 14) {
              return -10;
            }
            return items.data.length;
          });
          break;
        case 'size-desc':
          strategy = (rawData) => _.sortBy(rawData, items => -items.data.length);
          break;
        default:
          break;
      }

      const now = getNow();
      combinations = getCombos(cpArgs, {
        sortStrategy: strategy,
      });
      const duration = getNow() - now;

      const cartProductSize = Combinatorics.cartesianProduct(...cpArgs).length;

      results.push({
        strategy: strategyName,
        numCombos: combinations.length.toLocaleString(),
        calls: walkCalls.toLocaleString(),
        leaves: leavesExplored.toLocaleString(),
        explored: partialsExplored.toLocaleString(),
        cpLength: cartProductSize.toLocaleString(),
        pctEvaluated: _.round(100 * (leavesExplored / cartProductSize), 4),
        time: _.round(duration, 1).toLocaleString(),
        timePerExplored: _.round((duration / partialsExplored), 5),
      });
    });


    /*
    const now = getNow();
    cp.toArray();
    const duration = getNow() - now;

    results.push({
      strategy: 'js-combinatorics cartesianProduct',
      numCombos: cp.length,
      calls: null,
      time: _.round(duration, 1),
    });
    */
    //devTable(_.sortBy(results, 'time'));

    if (tooLarge) {
      return Infinity;
    }

    return combinations;

    //return cp;
  } catch (error) {
    // eslint-disable-next-line
    console.error(error);
    return [];
  }
}

const slotIndexMap = [
  'head',
  'neck',
  'shoulder',
  'back',
  'chest',
  'wrist',
  'hands',
  'waist',
  'legs',
  'feet',
  'finger1',
  'finger2',
  'trinket1',
  'trinket2',
  'main_hand',
  'off_hand',
];

let cacheStats;

// ugh, worst function params ever
// This function is also highly optimized and avoids lodash
// All the linting exceptions are intended to try to exit early whenever possible and reduce loops
// over the data.
export function getCombinations(
  cartesianProduct,
  onlyMaxLegendaries,
  tier21Pieces,
  tier20Pieces,
  tier19Pieces,
  lockedRing = null,
  lockedTrinket = null,
  classId,
  spec,
  selectedEnhancements,
  replaceEnhancements,
  forceMainStat,
  equippedGear,
  equippedTalents,
  equippedEssences,
  equippedSoulbind,
  enableRuneWords,
  renown,
  rawCombinationsMax,
) {
  const perfStart = Date.now();

  // rawCombinationMax could be explicit undefined from a selector
  const maxRaw = rawCombinationsMax || SharedConfig.optimize.maxCombinations;

  if (cartesianProduct === null) {
    return {
      combinations: [],
    };
  }
  if (cartesianProduct.length === 0) {
    return {
      combinations: [],
    };
  }
  if (cartesianProduct === Infinity) {
    return {
      combinations: null,
      rawCombos: Infinity,
    };
  }

  // things that can make combos > rawCombos (multipliers)
  //  * gems

  // things that can make combos < rawCombos (filters)
  //  * unique equipped
  //  * duplicate gem setups
  //  * duplicate enchant setups
  //  * duplicate azerite power setup

  if (cartesianProduct.length > maxRaw) {
    // generating combos will take significant time.
    // just bail out and use approximations
    return {
      combinations: null,
      rawCombos: cartesianProduct.length,
    };
  }

  let setKeyTime = 0;

  let equippedDomShardTier = 0;

  const fulfilledConstraints = {
    uniqueItems: false,
    canFillSockets: true,
    shardSetBonusGteEquipped: false,
  };

  cacheStats = {
    hit: 0,
    miss: 0,
    generateTime: 0,
  };

  const result = [];
  const gemCombinationCache = {};
  const usedItems = new Set();

  const specDetails = getSpecInfo(classId, spec) || {};

  const specSelectedEnhancements = _.filter(selectedEnhancements, { specId: specDetails.id });
  const selectedGems = _.map(
    _.filter(specSelectedEnhancements, { slot: 'socket' }),
    g => {
      const gem = _.find(gemInfo, { id: g.enhancementId });
      return {
        ...g,
        unique: gem?.unique || false,
        gem,
      };
    },
  );

  // We only "collect" sockets for action later if the user has selections for that socket type.
  // This prevents some cases where the gem combinator would remove gems in a surprising way
  const selectedGemsBySocketType = _.groupBy(selectedGems, 'gem.type');
  const collectSocketType = _.mapValues(selectedGemsBySocketType, gems => gems.length > 0);

  const specForceMainStat = _.get(_.find(forceMainStat, { specId: specDetails.id }), 'value', false);
  const setKeys = new Set();

  let tooManyCombos = false;

  const removals = {
    uniqueEquipped: 0,
    itemLimit: 0,
    uniqueGem: 0,
    lockedRing: 0,
    lockedTrinket: 0,
    setKey: 0,
    invalidGemCombo: 0,
  };

  const processGearset = (gearset, equipped = false) => {
    if (tooManyCombos || result.length > SharedConfig.optimize.maxTotalCombinations) {
      if (!tooManyCombos) {
        tooManyCombos = true;
      }
      return false;
    }

    // flatten to just get a simple array of items
    const set = [
      gearset[0],
      gearset[1],
      gearset[2],
      gearset[3],
      gearset[4],
      gearset[5],
      gearset[6],
      gearset[7],
      gearset[8],
      gearset[9],
      gearset[10][0], // ring 1
      gearset[10][1], // ring 2
      gearset[11][0], // trinket 1
      gearset[11][1], // trinket 2
      gearset[12][0],
      gearset[12][1] || {},

      // gearset[13] is selectedTalents
      // gearset[14] is azerite essences
      // gearset[15] is soulbinds
    ];

    const socketSlots = [];
    const filledSocketSlots = [];
    const uniqueGemsUsed = new Set();

    for (let i = 0; i < set.length; i += 1) {
      const item = set[i];

      if (!item) {
        // eslint-disable-next-line no-continue
        continue;
      }

      _.each(item.socketInfo, (socketInfo, socketType) => {
        if (collectSocketType[socketType] && socketInfo.total > 0) {
          if (replaceEnhancements || socketInfo.filled === 0) {
            socketSlots.push({ idx: i, type: socketType });
          } else {
            filledSocketSlots.push({ idx: i, type: socketType, socketInfo });
            if (item.parsed.uniqueGems.length > 0) {
              for (let j = 0; j < item.parsed.uniqueGems.length; j += 1) {
                const uniqueGem = item.parsed.uniqueGems[j];
                if (uniqueGemsUsed.has(uniqueGem.unique)) {
                  return false;
                }
                uniqueGemsUsed.add(uniqueGem.unique);
              }
            }
          }
        }
        return true;
      });
    }

    // check for locked trinket and ring
    if (lockedRing !== null) {
      if (set[10].origIdx !== lockedRing && set[11].origIdx !== lockedRing) {
        removals.lockedRing += 1;
        return false;
      }
    }
    if (lockedTrinket !== null && set[12] && set[13]) {
      if (set[12].origIdx !== lockedTrinket && set[13].origIdx !== lockedTrinket) {
        removals.lockedTrinket += 1;
        return false;
      }
    }

    const gear = {
      head: gearset[0],
      neck: gearset[1],
      shoulder: gearset[2],
      back: gearset[3],
      chest: gearset[4],
      wrist: gearset[5],
      hands: gearset[6],
      waist: gearset[7],
      legs: gearset[8],
      feet: gearset[9],
      finger1: gearset[10][0],
      finger2: gearset[10][1],
      trinket1: gearset[11][0],
      trinket2: gearset[11][1],
      main_hand: gearset[12][0],
      off_hand: gearset[12][1],
    };

    // we now have the base gear, time to figure out gem/corruption variation candidates

    const candidates = [];

    ////////////////////////////////////////////////////////////////
    //
    // fill in gem slots
    //

    // TODO: gem combos do not handle items with multiple sockets correctly
    // it basically only pays attention to 1 socket at a time

    let gemCombos = [null];
    const numSockets = socketSlots.length;
    const socketSlotsSorted = _.sortBy(socketSlots, 'type');
    if (!equipped) {
      if (numSockets > 0 && specSelectedEnhancements && selectedGems.length > 0) {
        const socketLayout = _.map(socketSlotsSorted, 'type');
        const filledSocketLayout = _.filter(_.map(filledSocketSlots, slot => {
          return slot.socketInfo?.gems[0].unique;
        }));
        const socketLayoutKey = [
          ...filledSocketLayout.sort(),
          ...socketLayout,
        ].join('-');
        if (!gemCombinationCache[socketLayoutKey]) {
          let forceMain = specForceMainStat;
          let gems = selectedGems;
          // this is only populated if "replace existing" is not selected
          if (uniqueGemsUsed.size > 0) {
            forceMain = false;
            // make sure we remove possible gems that already have a unique version in the gear
            gems = _.reject(selectedGems, gem => {
              return uniqueGemsUsed.has(gem.unique);
            });
          }
          gemCombinationCache[socketLayoutKey] = getGemCombinations(
            gems,
            forceMain,
            socketLayout,
            filledSocketSlots,
            specDetails,
            enableRuneWords,
          );
        }
        gemCombos = gemCombinationCache[socketLayoutKey];
        if (gemCombos.length === 0) {
          fulfilledConstraints.canFillSockets = false;
        }
      }
    }

    for (let i = 0; i < gemCombos.length; i += 1) {
      if (tooManyCombos || result.length > SharedConfig.optimize.maxTotalCombinations) {
        if (!tooManyCombos) {
          tooManyCombos = true;
        }
        return false;
      }

      // get a copy of this array because we gonna mutate it
      const gemCombo = gemCombos[i] && gemCombos[i].slice();

      let comboGear = gear;
      let validGemCombo = true;

      if (gemCombo) {
        // match up gems that already match so we don't just shift gems around
        const slotsToFill = [];
        for (let j = 0; j < numSockets; j += 1) {
          const slotIndex = socketSlotsSorted[j].idx;
          const slotType = socketSlotsSorted[j].type;
          const slotName = slotIndexMap[slotIndex];
          const slotItem = comboGear[slotName];

          // does this item have this type of socket?
          const itemTypeSocket = slotItem.socketInfo[slotType];
          if (itemTypeSocket?.total > 0) {
            for (let g = 0; g < itemTypeSocket.total; g += 1) {
              const slotItemGem = itemTypeSocket.gemIds[g];
              const gemComboIdx = _.indexOf(gemCombo, { gem: slotItemGem, type: slotType });
              if (gemComboIdx > -1) {
                // remove gem from combo, already in gear
                gemCombo.splice(gemComboIdx, 1);
              } else {
                slotsToFill.push({ idx: slotIndex, type: slotType });
              }
            }
          }
        }

        // fill the rest of the slots
        if (slotsToFill.length > 0) {
          // sort gems and sockets in such a way that highest ilevel items get main stat gem
          /*
          const sortedSlotsToFill = _.sortBy(
            slotsToFill,
            slotIndex => {
              const slotName = slotIndexMap[slotIndex];
              return _.get(comboGear[slotName], 'itemLevel');
            },
          );
          */

          for (let j = 0; j < slotsToFill.length; j += 1) {
            const slotIndex = slotsToFill[j].idx;
            const slotType = slotsToFill[j].type;
            const slotName = slotIndexMap[slotIndex];

            const targetItem = comboGear[slotName];
            if (!gemCombo[j]) {
              // TODO: Handle multiple prismatic sockets correctly
              // As far as I can tell, ths only happens when an older item with multiple sockets is equipped
              // and "Replace Existing" is active. I think the slotsToFill code looks at multiple
              // sockets but getGemCombinations does not
              // eslint-disable-next-line no-continue
              continue;
            }
            const candidateGemId = gemCombo[j].gem;

            // TODO: Check gem unique property, not gem ID
            if (uniqueGemsUsed.has(candidateGemId)) {
              validGemCombo = false;
              break;
            }

            // TODO: Handle multiple sockets
            const newGemId = _.join(
              _.filter(
                _.map(targetItem.socketInfo, (socketInfo, socketType) => {
                  if (socketType === slotType) {
                    return candidateGemId;
                  }
                  const existingGem = _.find(targetItem.parsed.gems, { type: socketType });
                  return existingGem ? String(existingGem.id) : null;
                }),
              ),
              '/',
            );

            // do some careful copying here for perf

            const override = {
              ...targetItem.override,
              gem_id: newGemId,
            };
            const item = decorateItem({
              ...makeNewCacheItem(targetItem),
              gem_id: newGemId,
              override,
              // create a new cache item
            });
            comboGear = {
              ...comboGear,
              [slotName]: item,
            };
          }
        }
      }

      if (validGemCombo) {
        candidates.push(comboGear);
      } else {
        removals.invalidGemCombo += 1;
      }
    }

    ////////////////////////////////////////////////////////////////
    //
    // De-duplicate new candidate(s) against the global setKeys
    //

    const setKeyStart = getNow();
    _.each(candidates, candidateGear => {
      const overrides = [];
      _.each(candidateGear, (item, slot) => {
        if (item && item.override) {
          overrides.push({
            slot,
            override: item.override,
          });
        }
      });

      let combo;
      // switch for compatibility for sept 26 talent combination update
      if (gearset[13]) {
        // new version, has talents
        let essences;
        let soulbind;
        let covenant;
        if (gearset[14]) {
          if (_.has(gearset[14], 'corruptionReduction')) {
            essences = gearset[14].str;
          } else {
            soulbind = gearset[14].str;
            covenant = gearset[14].covenant;
          }
        }
        combo = {
          gear: candidateGear,
          talents: gearset[13],
          essences,
          overrides: overrides.length > 0 ? overrides : undefined,
          soulbind,
          covenant,
        };
      } else {
        // old version, just gear
        combo = candidateGear;
      }

      ////////////////////////////////////////////////////////////////
      //
      // generate a "key" for this gearset that will remove things like duplicate ring enchants
      // Need to include anything a user can change/select
      //  * selected items
      //  * azerite powers
      //  * weapon enchant
      //  * ring enchants
      //  * talents
      //
      // Gem combos happen after this so we can ignore them in this bit
      //
      // THIS MIGHT BREAK WHEN AZERITE GOES AWAY
      // YOU'LL BE BACK
      //

      const gearCheck = combo.gear;
      if (gearCheck) {
        const selectedIndexes = [];
        const gems = [];
        // TODO: Maybe generalize this a little bit more, have some kidn of category of "simple stat gem/enchant"
        const ringGemStats = {
          crit: 0,
          haste: 0,
          mastery: 0,
          vers: 0,
        };
        _.each(gearCheck, (item, slot) => {
          if (item) {
            selectedIndexes.push(item.selectedIdx);
            if (item.gem_id) {
              if (item.socketInfo?.PRISMATIC) {
                const statGem = item.socketInfo.PRISMATIC.gems[0];
                const stat = statGem?.stat?.type;
                if (stat) {
                  ringGemStats[stat] += statGem.stat?.amount;
                }
              } else {
                gems.push(item.gem_id);
              }
            }
            if ((slot === 'finger1' || slot === 'finger2') && item.enchant_id) {
              const statInfo = item.parsed?.enchant?.stat;
              if (statInfo) {
                ringGemStats[statInfo.type] += statInfo.amount;
              }
            }
          }
        });


        let soulbindKey = null;
        if (combo.soulbind) {
          const soulbindParts = combo.soulbind.split(',');
          if (soulbindParts[1]) {
            soulbindKey = soulbindParts[1].split('/').sort().join('/');
          }
        }

        const setKey = [
          // item IDs
          selectedIndexes.join('/'),
          // azerite powers
          [
            ...(gearCheck.head ? gearCheck.head.parsed.azerite_powers : [0]),
            ...(gearCheck.shoulder ? gearCheck.shoulder.parsed.azerite_powers : [0]),
            ...(gearCheck.chest ? gearCheck.chest.parsed.azerite_powers : [0]),
          ].sort().join('/'),

          // enchants
          (gearCheck.main_hand && gearCheck.main_hand.parsed && gearCheck.main_hand.parsed.enchant_id) || 0,
          (gearCheck.off_hand && gearCheck.off_hand.parsed && gearCheck.off_hand.parsed.enchant_id) || 0,
          (gearCheck.back && gearCheck.back.parsed && gearCheck.back.parsed.enchant_id) || 0,
          (gearCheck.hands && gearCheck.hands.parsed && gearCheck.hands.parsed.enchant_id) || 0,
          (gearCheck.chest && gearCheck.chest.parsed && gearCheck.chest.parsed.enchant_id) || 0,
          (gearCheck.wrist && gearCheck.wrist.parsed && gearCheck.wrist.parsed.enchant_id) || 0,
          (gearCheck.feet && gearCheck.feet.parsed && gearCheck.feet.parsed.enchant_id) || 0,

          // gems
          gems.sort().join('/'),

          // stats from simple gems/enchants
          ringGemStats.crit,
          ringGemStats.haste,
          ringGemStats.mastery,
          ringGemStats.vers,

          combo.talents,
          combo.essences,
          soulbindKey,
        ].join('|');

        // add combination if not already seen
        if (!setKeys.has(setKey)) {
          setKeys.add(setKey);

          // add items to usedItems cache
          _.each(combo.gear, (item, slot) => {
            if (item) {
              usedItems.add(`${slot}:${item.selectedIdx}`);
            }
          });

          result.push(combo);
        } else {
          removals.setKey += 1;
        }

        // check dom shard set bonus rank
        const domSetBonus = getDominationShardSetBonus(combo.gear);

        // running check to see if we have a combo that meets or exceeds the equipped domination shard set bonus
        if (!fulfilledConstraints.shardSetBonusGteEquipped) {
          if (equipped) {
            if (domSetBonus) {
              // store the equipped set bonus
              equippedDomShardTier = domSetBonus.rank;
            } else {
              // no set bonus on equipped, anything goes
              fulfilledConstraints.shardSetBonusGteEquipped = true;
            }
          } else if (domSetBonus) {
            if (domSetBonus.rank >= equippedDomShardTier) {
              // we have a combo that meets or exceeds
              fulfilledConstraints.shardSetBonusGteEquipped = true;
            } else {
              // keep looking
            }
          }
        }
      }
    });
    setKeyTime += getNow() - setKeyStart;

    return true;
  };

  // only prepend equipped if frontend sent it
  if (equippedGear) {
    let expansionSlot;
    if (equippedSoulbind) {
      const soulbind = parseSoulbindSimcString(equippedSoulbind, renown);
      expansionSlot = {
        str: equippedSoulbind,
        path: '',
        covenant: getCovenant(soulbind?.covenantId),
      };
    } else if (equippedEssences) {
      expansionSlot = {
        str: stringifyAzeriteEssences(parseAzeriteEssences(equippedEssences)),
        corruptionReduction: 0,
      };
    }
    const equipped = [
      decorateItem(equippedGear.head[0]),
      decorateItem(equippedGear.neck[0]),
      decorateItem(equippedGear.shoulder[0]),
      decorateItem(equippedGear.back[0]),
      decorateItem(equippedGear.chest[0]),
      decorateItem(equippedGear.wrist[0]),
      decorateItem(equippedGear.hands[0]),
      decorateItem(equippedGear.waist[0]),
      decorateItem(equippedGear.legs[0]),
      decorateItem(equippedGear.feet[0]),
      _.map(equippedGear.rings, decorateItem),
      _.map(equippedGear.trinkets, decorateItem),
      _.map([equippedGear.main_hand[0], equippedGear.off_hand[0]], decorateItem),
      equippedTalents,
      expansionSlot,
    ];

    processGearset(equipped, true);
  }

  cartesianProduct.forEach(gearset => processGearset(gearset, false));

  ////////////////////////////////////////////////////////////////
  //
  // finish up
  //

  // unable to make combos because of a gem issue
  if (result.length === 0) {
    fulfilledConstraints.canFillSockets = false;
  }

  const perfEnd = Date.now();
  const duration = perfEnd - perfStart;

  if (tooManyCombos) {
    return {
      combinations: null,
      fulfilledConstraints,
      removals,
      stats: null,
    };
  }

  const stats = {
    rawCombos: cartesianProduct.length,
    filteredCombos: result.length,
    time: duration,
    cacheStats,
    setKeyTime,
  };

  /*
  console.table(_.reverse(_.sortBy(_.mapValues(timings, (timing, key) => ({
    key,
    time: _.round(timing, 1),
    pct: _.round(100 * (timing / duration), 1),
  })), 'time')));
  */
  //console.log(stats);

  const slotMap = {
    finger1: 'rings',
    finger2: 'rings',
    trinket1: 'trinkets',
    trinket2: 'trinkets',
  };

  return {
    combinations: result,
    fulfilledConstraints,
    removals,
    stats,
    usedItems: _.map([...usedItems], key => {
      const parts = _.split(key, ':');
      return {
        slot: slotMap[parts[0]] || parts[0],
        index: parseInt(parts[1], 10),
      };
    }),
  };
}
